import { SearchResult, TeamNode } from './MoveTeamModal.types';

/**
 * Recursively maps a team object to a TeamNode structure.
 *
 * @param team - The team object to map. Should contain at least `_id`, `name`, and optionally `children`.
 * @returns A TeamNode object with `id`, `name`, and recursively mapped `children` if present.
 */
export const mapTeamToNode = (team: any): TeamNode => {
  return {
    id: team._id,
    name: team.name,
    children: team.children ? team.children.map(mapTeamToNode) : undefined,
  };
};

/**
 * Recursively finds the path from root to a team with the given ID.
 * Returns the path as an array of TeamNode objects, or null if not found.
 */
export const findPathToTeam = (
  nodes: TeamNode[],
  targetId: string,
  currentPath: TeamNode[] = [],
): TeamNode[] | null => {
  for (const node of nodes) {
    const newPath = [...currentPath, node];

    if (node.id === targetId) {
      return newPath;
    }

    if (node.children) {
      const found = findPathToTeam(node.children, targetId, newPath);

      if (found) return found;
    }
  }

  return null;
};

/**
 * Recursively searches all teams and returns matches with their full paths.
 */
export const searchAllTeams = (
  nodes: TeamNode[],
  query: string,
  currentPath: TeamNode[] = [],
  excludeTeamId?: string | null,
): SearchResult[] => {
  const results: SearchResult[] = [];
  const queryLower = query.toLowerCase();

  for (const node of nodes) {
    const nodePath = [...currentPath, node];

    // Check if this node matches (exclude the team being moved)
    if (node.name.toLowerCase().includes(queryLower) && node.id !== excludeTeamId) {
      results.push({ team: node, path: nodePath });
    }

    // Recursively search children
    if (node.children) {
      results.push(...searchAllTeams(node.children, query, nodePath, excludeTeamId));
    }
  }

  return results;
};
